import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { practiceVaccineService } from '../services/practiceVaccineService'
import { PracticeVaccine, PracticeVaccineFormData } from '../types/practiceVaccine'
import { PracticeVaccineInventory } from '../components/PracticeVaccineInventory'
import './SettingsPracticeVaccines.css'

export function SettingsPracticeVaccines() {
  const queryClient = useQueryClient()
  const [editingVaccine, setEditingVaccine] = useState<PracticeVaccine | null>(null)
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['practice-vaccines'],
    queryFn: () => practiceVaccineService.getPracticeVaccines(),
  })

  const { data: expiredVaccines } = useQuery({
    queryKey: ['practice-vaccines-expired'],
    queryFn: () => practiceVaccineService.getExpiredVaccines(),
  })

  const createMutation = useMutation({
    mutationFn: (formData: PracticeVaccineFormData) =>
      practiceVaccineService.createPracticeVaccine(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-vaccines'] })
      queryClient.invalidateQueries({ queryKey: ['practice-vaccines-expired'] })
      setShowForm(false)
      setEditingVaccine(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ vaccineId, formData }: { vaccineId: string; formData: Partial<PracticeVaccineFormData & { isActive?: boolean; quantity?: number }> }) =>
      practiceVaccineService.updatePracticeVaccine(vaccineId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-vaccines'] })
      queryClient.invalidateQueries({ queryKey: ['practice-vaccines-expired'] })
      setEditingVaccine(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (vaccineId: string) => practiceVaccineService.deletePracticeVaccine(vaccineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-vaccines'] })
      queryClient.invalidateQueries({ queryKey: ['practice-vaccines-expired'] })
    },
  })

  const handleEdit = (vaccine: PracticeVaccine) => {
    setEditingVaccine(vaccine)
    setShowForm(true)
  }

  const handleDelete = async (vaccine: PracticeVaccine) => {
    if (window.confirm(`Are you sure you want to inactivate ${vaccine.vaccineType} (batch: ${vaccine.batchNumber})?`)) {
      await deleteMutation.mutateAsync(vaccine.vaccineId)
    }
  }

  const handleFormSubmit = async (formData: PracticeVaccineFormData) => {
    if (editingVaccine) {
      await updateMutation.mutateAsync({ vaccineId: editingVaccine.vaccineId, formData })
    } else {
      await createMutation.mutateAsync(formData)
    }
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingVaccine(null)
  }

  if (isLoading) {
    return <div className="settings-practice-vaccines">Loading practice vaccines...</div>
  }

  if (error) {
    return <div className="settings-practice-vaccines error">Error loading practice vaccines: {String(error)}</div>
  }

  return (
    <div className="settings-practice-vaccines">
      <div className="settings-header">
        <h1>Practice Vaccine Inventory</h1>
        <p>Manage vaccine stock, batch numbers, and expiry dates for your practice</p>
      </div>

      {expiredVaccines && expiredVaccines.items.length > 0 && (
        <div className="expired-vaccines-alert">
          <h3>⚠️ Expired Vaccines</h3>
          <p>You have {expiredVaccines.items.length} expired vaccine batch(es) that should be inactivated.</p>
          <ul>
            {expiredVaccines.items.slice(0, 5).map((v) => (
              <li key={v.id}>
                {v.vaccineType} - Batch: {v.batchNumber} - Expired: {v.expiryDate}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PracticeVaccineInventory
        vaccines={data?.items || []}
        onAdd={() => {
          setEditingVaccine(null)
          setShowForm(true)
        }}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        showForm={showForm}
        editingVaccine={editingVaccine}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
